import {
  App,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Rate,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Grid,
  Card,
  theme,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Category, Prompt } from '../../models/models';
import { setPromptData } from '../../redux/slices/promptSelectionSlice';
import { RootState } from '../../redux/store';
import { setMenuSelection } from '../../redux/slices/menuSelectionSlice';
import { useTranslation } from 'react-i18next';

const { Text, Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const getLocalizedListEntry = (source: any, language: string, index: number): string => {
  if (!source) return '';
  const languagePriority = Array.from(new Set([language, 'es', 'en']));

  if (Array.isArray(source)) {
    const entry = source[index];
    if (entry && typeof entry === 'object') {
      for (const lang of languagePriority) {
        const value = entry?.[lang];
        if (typeof value === 'string' && value.length) {
          return value;
        }
      }
      const fallback = Object.values(entry).find((val) => typeof val === 'string' && val.length);
      if (typeof fallback === 'string') return fallback;
    } else if (typeof entry === 'string' && entry.length) {
      return entry;
    }
    return '';
  }

  if (typeof source === 'object') {
    for (const lang of languagePriority) {
      const arr = source?.[lang];
      if (Array.isArray(arr) && typeof arr[index] === 'string' && arr[index].length) {
        return arr[index];
      }
    }
    const fallbackArr = Object.values(source).find(
      (val) => Array.isArray(val) && typeof val[index] === 'string' && val[index].length,
    ) as string[] | undefined;
    return fallbackArr?.[index] ?? '';
  }

  return '';
};

interface PromptCardProps {
  categories: Category[];
  category: string;
  prompt: Prompt;
  aiModels: any[];
  clientId: string | null | undefined;
}

const PromptCard: React.FC<PromptCardProps> = ({
  categories,
  category,
  prompt,
  aiModels,
}) => {
  const screens = useBreakpoint();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [promptCategories, setPromptCategories] = useState<string[]>([]);
  const [model, setModel] = useState<string | null>(null);
  const [suggestedTechnology, setSuggestedTechnology] = useState<string>('ChatGPT');

  const dispatch = useDispatch();
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const { t, i18n } = useTranslation();


  console.log("prompt", prompt)

  const showModal = () => {
    console.log("promptSelected", prompt)
    const suggested: string =
      (prompt?.suggestedTechnology &&
        aiModels.some(m => m.value.toLowerCase() === prompt.suggestedTechnology?.toLowerCase()))
        ? prompt.suggestedTechnology
        : aiModels.length > 0 ? aiModels[0].value : 'ChatGPT';

    setSuggestedTechnology(suggested);
    if (!model) setModel(suggested);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  function handleModelChange(value: string) {
    setModel(value);
  }

  const aiModelsSelectOptions = aiModels.map((m) => ({
    value: m.value,
    label: (
      <Text strong>
        {suggestedTechnology && m.value === suggestedTechnology ? `${m.value} (${t('prompts.recommended')})` : m.value}
      </Text>
    ),
  }));

  const handleOk = () => {
    form.validateFields()
      .then(values => {
        let filledPrompt = prompt.prompt[i18n.language];
        prompt.variables.forEach((variable, index) => {
          const cleanVariable = variable.replace(/,/g, '').trim();
          const regex = new RegExp(`@${cleanVariable}`, 'g');
          filledPrompt = filledPrompt.replace(regex, values[variable]);
        });

        const promptName = prompt.name?.[i18n.language];
        const chatName = promptName.length > 23 ? `${promptName.substring(0, 20)}...` : promptName;

        const promptData = {
          promptId: prompt.id,
          prompt: filledPrompt,
          category: category,
          chatName,
          model,
        };

        dispatch(setPromptData(promptData));
        dispatch(setMenuSelection('chat'));
        message.success(t('prompts.promptSentSuccessfully'));
        handleCancel();
      })
      .catch(() => { });
  };

  useEffect(() => {
    if (!aiModels || aiModels.length === 0) return;
    setModel(aiModels[0].value ?? 'ChatGPT');
  }, [aiModels]);

  useEffect(() => {
    const promptId = prompt.id;
    const filtered = categories.filter((cat) => cat.prompts.includes(promptId!));
    console.log("Promptcategories", categories)
    setPromptCategories(filtered.map((cat) => cat.name?.[i18n.language] || cat.name?.es || cat.id));
  }, [categories, category, prompt, i18n.language]);

  return (
    <>
      {/* CARD SIN MEDIDAS FIJAS: se adapta al contenido */}
      <Card
        hoverable
        size="small"
        style={{
          borderRadius: 12,
          height: '100%',
          background: isDarkMode ? token.colorBgContainer : '#fff',
        }}
        styles={{ body: { padding: 16, display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
          {/* Tags de categorías */}
          {promptCategories.length > 0 && (
            <Space wrap size={[6, 6]}>
              {promptCategories.map((cat, idx) => (
                <Tag key={idx} bordered={false}>{cat}</Tag>
              ))}
            </Space>
          )}

          {/* Título */}
          <Title
            level={screens.xs ? 5 : 4}
            style={{ margin: 0, lineHeight: 1.25 }}
          >
            {prompt.name?.[i18n.language] || prompt.name?.es || ''}
          </Title>

          {/* Descripción adaptable (con expandible opcional) */}
          <Paragraph
            type="secondary"
            style={{ margin: 0 }}
            ellipsis={{ rows: screens.xs ? 3 : 2, expandable: true, symbol: t('prompts.seeMore') }}
          >
            {prompt.description?.[i18n.language] || prompt.description?.es || ''}
          </Paragraph>

          {/* Spacer para empujar contenido hacia abajo */}
          <div style={{ flex: 1 }} />

          <Row align="middle" justify="space-between" gutter={[12, 12]}>
            <Col>
              <Space direction="vertical" size={2}>
                <Text strong>{(prompt.rateValue ?? 0).toFixed(2)}</Text>
                <Rate allowHalf value={prompt.rateValue ?? 0} disabled style={{ fontSize: 14 }} />
              </Space>
            </Col>
            <Col>
              <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
                <Text strong>{prompt.count ?? 0}</Text>
                <Text type="secondary">{t('prompts.timesUsed')}</Text>
              </Space>
            </Col>
          </Row>

          {/* CTA */}
          <Button type="primary" block onClick={showModal}>
            {t('prompts.usePrompt')}
          </Button>
        </div>
      </Card>

      {/* Modal de variables */}
      <Modal
        title={
          <div>
            <Title level={4} style={{ marginBottom: 0 }}>{prompt.name?.[i18n.language] || prompt.name?.es || ''}</Title>
            <Text style={{ fontWeight: 400 }}>{prompt.description?.[i18n.language] || prompt.description?.es || ''}</Text>
          </div>
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => { form.resetFields(); handleCancel(); }}
        okText={t('prompts.accept')}
        cancelText={t('common.cancel')}
        styles={{
          header: { padding: screens.xs ? 16 : 24 },
          body: { padding: screens.xs ? 16 : 24 },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleOk();
            }
          }}
        >
          {prompt.variables.map((variable, index) => {
            const label = getLocalizedListEntry(prompt.values, i18n.language, index) || variable;
            const placeholder = getLocalizedListEntry(prompt.placeholders, i18n.language, index) || label;
            return (
              <Form.Item
                key={index}
                label={label}
                name={variable}
                rules={[{ required: true, message: t('prompts.pleaseEnter', { field: label }) }]}
              >
                <Input placeholder={placeholder} />
              </Form.Item>
            );
          })}

          <Form.Item
            key="model"
            label={t('prompts.aiModel')}
            name="ai_model"
            rules={[{ required: true, message: t('prompts.pleaseSelectAiModel') }]}
          >
            <Select
              value={model ?? suggestedTechnology}
              onChange={handleModelChange}
              options={aiModelsSelectOptions}
              style={{ width: '100%' }}
              placeholder={t('prompts.aiModel')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PromptCard;
